import {
  caseFormSectionsLabels,
  formFieldAllowedInputs,
  newObjects,
  specialFieldBehaviors,
  typeKeys,
} from '@messenger/consts'
import { EDITOR_FIELD_CATALOG } from '@messenger/lotus'
import {
  additionalFamilyTypes,
  familyTypeKey,
  formatFamilyType,
  isBucketFamilyType,
  orderedBySequence,
} from '@messenger/utils'
import orderBySequence from '@/utils/caseDetails/orderBySequence'

/** One input of a row, as the case form declares it. */
export type TabularRowInput = { field: string; type: 'input' | 'check' }

/** A row as the case form holds it: the inputs' values plus whatever else the row carries. */
export type TabularRow = Record<string, unknown>

/**
 * How a tabular field's rows are authored: the case column they live in and
 * the inputs the case form shows for one row. The inputs and the blank row
 * are the form's own declarations (`formFieldAllowedInputs`, `newObjects`),
 * so a modal built from this offers exactly what the form offers.
 */
export type TabularRowForm = {
  fieldKey: string
  column: string
  /** The column holds plain strings (the songs, the family name lists): one text input per row. */
  plainStrings: boolean
  /** The inputs shown per row, the hidden ones (`sequence`) left out as the form leaves them out. */
  inputs: readonly TabularRowInput[]
  /** `caseFormSectionsLabels` key naming one row — "Pallbearer", "Song" — and its English reading. */
  rowLabelKey: string
  rowLabel: string
  /** When several kinds of row share the column, the `type` this field's rows carry. */
  rowType?: string
  /** A blank row; an additional-family row takes the type of the group its table names, else the bucket's. */
  newRow: (rowType?: string) => TabularRow
}

const isRecord = (value: unknown): value is TabularRow =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const str = (value: unknown): string => (typeof value === 'string' ? value : '')

const FAMILY_ROWS_COLUMN = 'additional_family'

const shownInputs = (
  inputs: readonly TabularRowInput[],
): readonly TabularRowInput[] =>
  inputs.filter(
    (input) => input.field !== specialFieldBehaviors.hidden.sequence,
  )

const NAME_INPUT: readonly TabularRowInput[] = [
  { field: 'name', type: 'input' },
]

const plainList = (
  fieldKey: string,
  rowLabelKey: string,
  rowLabel: string,
): TabularRowForm => ({
  fieldKey,
  column: fieldKey,
  plainStrings: true,
  inputs: NAME_INPUT,
  rowLabelKey,
  rowLabel,
  newRow: () => ({ name: '' }),
})

const PALLBEARER_INPUTS = shownInputs(formFieldAllowedInputs.pallbearers)

const ROW_FORMS: readonly TabularRowForm[] = [
  plainList('clergy', 'clergy', caseFormSectionsLabels.clergy),
  plainList('children', 'child', caseFormSectionsLabels.child),
  plainList('siblings', 'sibling', caseFormSectionsLabels.sibling),
  plainList('songs', 'song', caseFormSectionsLabels.song),
  {
    fieldKey: 'pallbearers',
    column: 'pallbearers',
    plainStrings: false,
    inputs: PALLBEARER_INPUTS,
    rowLabelKey: typeKeys.pallbearer,
    rowLabel: formatFamilyType(typeKeys.pallbearer),
    rowType: typeKeys.pallbearer,
    newRow: () => ({ ...newObjects.pallbearers, type: typeKeys.pallbearer }),
  },
  {
    fieldKey: 'honorary_pallbearers',
    column: 'pallbearers',
    plainStrings: false,
    inputs: PALLBEARER_INPUTS,
    rowLabelKey: typeKeys.honoraryPallbearer,
    rowLabel: formatFamilyType(typeKeys.honoraryPallbearer),
    rowType: typeKeys.honoraryPallbearer,
    newRow: () => ({
      ...newObjects.pallbearers,
      type: typeKeys.honoraryPallbearer,
    }),
  },
  {
    fieldKey: 'memorial_contributions',
    column: 'memorial_contributions',
    plainStrings: false,
    inputs: shownInputs(formFieldAllowedInputs.memorialContributions),
    rowLabelKey: typeKeys.memorialContribution,
    rowLabel: formatFamilyType(typeKeys.memorialContribution),
    newRow: () => ({ ...newObjects.memorialContributions }),
  },
  {
    fieldKey: 'organizations',
    column: 'organizations',
    plainStrings: false,
    inputs: shownInputs(formFieldAllowedInputs.organizations),
    rowLabelKey: typeKeys.organization,
    rowLabel: formatFamilyType(typeKeys.organization),
    newRow: () => ({ ...newObjects.organizations }),
  },
  {
    fieldKey: 'additional_family',
    column: 'additional_family',
    plainStrings: false,
    inputs: formFieldAllowedInputs.additionalFamily.map(
      (field): TabularRowInput => ({ field, type: 'input' }),
    ),
    rowLabelKey: 'additionalFamily',
    rowLabel: caseFormSectionsLabels.additionalFamily,
    newRow: (rowType) => ({
      ...newObjects.additionalFamily,
      type: rowType ?? additionalFamilyTypes.additionalFamily,
    }),
  },
]

/** The keys of every field the menu offers as a table. */
export const TABULAR_FIELD_KEYS: readonly string[] =
  EDITOR_FIELD_CATALOG.filter((entry) => entry.kind === 'tabular').map(
    (entry) => entry.key,
  )

/** The row form of a tabular field; null for every other field key. */
export const tabularRowForm = (fieldKey: string): TabularRowForm | null =>
  ROW_FORMS.find((form) => form.fieldKey === fieldKey) ?? null

/**
 * The honorary rows of the pallbearers relation. Every other row — typed
 * "pallbearer", or carrying no type at all — is a regular pallbearer, so
 * the two tables between them print the whole relation once.
 */
export const isHonoraryPallbearerRow = (row: TabularRow): boolean =>
  familyTypeKey(str(row.type)) === typeKeys.honoraryPallbearer

const rowBelongsTo = (form: TabularRowForm, row: TabularRow): boolean =>
  form.rowType === undefined ||
  isHonoraryPallbearerRow(row) ===
    (form.rowType === typeKeys.honoraryPallbearer)

/**
 * The group of the case form's Additional Family section a row sits in, as a
 * table names it: every bucket type ('', 'other', 'additional family',
 * 'additional_family') is the canonical bucket, any other type its
 * case-insensitive key — the key the form dedupes its groups by.
 */
export const additionalFamilyRowType = (row: TabularRow): string =>
  isBucketFamilyType(str(row.type))
    ? additionalFamilyTypes.additionalFamily
    : familyTypeKey(str(row.type))

/**
 * The additional-family groups of a case's rows, as the menu lists them: the
 * named groups once each, in the order their first rows appear, after the
 * bucket when some row is a bucket row or no row is in a named group. An
 * empty case and an M3 order's flat rows each list the bucket alone; a case
 * whose rows are all in named groups does not list it.
 */
export const additionalFamilyRowTypes = (
  rows: readonly TabularRow[],
): string[] => {
  const bucket: string = additionalFamilyTypes.additionalFamily
  const rowTypes = rows.map(additionalFamilyRowType)
  const named = [...new Set(rowTypes.filter((rowType) => rowType !== bucket))]
  return named.length === 0 || rowTypes.includes(bucket)
    ? [bucket, ...named]
    : named
}

/** Whether a table holds a row: an additional-family table naming a group holds that group's rows only. */
const inRowGroup = (
  form: TabularRowForm,
  row: TabularRow,
  rowType: string | undefined,
): boolean =>
  rowType === undefined ||
  form.column !== FAMILY_ROWS_COLUMN ||
  additionalFamilyRowType(row) === rowType

/** The rows a column holds, a bare string (the legacy pallbearer shape) read as a named row. */
const rowsOfColumn = (
  caseRecord: Record<string, unknown>,
  column: string,
): TabularRow[] => {
  const value = caseRecord[column]
  if (!Array.isArray(value)) return []
  return value
    .map((item) => (typeof item === 'string' ? { name: item } : item))
    .filter(isRecord)
}

/**
 * The rows of the case this field's table prints, each as the case form
 * holds it; an additional-family table naming a group prints that group's,
 * and the pallbearer tables print in the order the rows' sequences give.
 */
export const tabularRowsFromCase = (
  form: TabularRowForm,
  caseRecord: Record<string, unknown>,
  rowType?: string,
): TabularRow[] => {
  if (form.plainStrings) {
    const value = caseRecord[form.column]
    return Array.isArray(value)
      ? value
          .filter((item): item is string => typeof item === 'string')
          .map((name) => ({ name }))
      : []
  }
  const rows = rowsOfColumn(caseRecord, form.column).filter(
    (row) => rowBelongsTo(form, row) && inRowGroup(form, row, rowType),
  )
  return form.column === 'pallbearers' ? orderedBySequence(rows) : rows
}

const trimmedName = (row: TabularRow): string => str(row.name).trim()

/** The rows with a name, names trimmed — the server drops a nameless row, so the write does too. */
const namedRows = (rows: readonly TabularRow[]): TabularRow[] =>
  rows
    .map((row) => ({ ...row, name: trimmedName(row) }))
    .filter((row) => row.name !== '')

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []

const FAMILY_LIST_COLUMNS: readonly string[] = [
  'children',
  'siblings',
  'clergy',
]

/**
 * The server rebuilds the whole additional-family relation from one PATCH:
 * the three name lists it splits out of that relation and the rows that
 * remain travel together, or the rows left out are deleted. The case form
 * sends all four on every save; so does a write of any one of them.
 */
const wholeFamily = (
  caseRecord: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> => ({
  children: stringList(caseRecord.children),
  siblings: stringList(caseRecord.siblings),
  clergy: stringList(caseRecord.clergy),
  [FAMILY_ROWS_COLUMN]: rowsOfColumn(caseRecord, FAMILY_ROWS_COLUMN),
  ...patch,
})

/**
 * The sequences the case form writes: the rows in the order of the
 * sequences they carry, a row without one after them, then renumbered from
 * 1. Copies first — the form's helper assigns into a row it is given.
 */
const sequenced = (rows: readonly TabularRow[]): TabularRow[] =>
  orderBySequence(
    rows.map((row) => ({ ...row })) as Array<TabularRow & { sequence: string }>,
  )

/** A bucket row as the write stores it: its own type trimmed, else the canonical bucket. */
const bucketTyped = (row: TabularRow): TabularRow => ({
  ...row,
  type: str(row.type).trim() || additionalFamilyTypes.additionalFamily,
})

/**
 * The additional-family rows a write stores. A write naming no group is
 * the whole column, each row typed as a bucket row unless it carries a
 * type. A write naming a group replaces that group's rows only: every
 * other group's rows stay exactly as the case holds them, the group's rows
 * stand where its first row stood, so the groups keep their order, and a
 * named group's rows carry the group as their type.
 */
const familyRowsWithGroup = (
  caseRecord: Record<string, unknown>,
  kept: readonly TabularRow[],
  rowType: string | undefined,
): TabularRow[] => {
  if (rowType === undefined) return kept.map(bucketTyped)
  const group = kept.map((row) =>
    rowType === additionalFamilyTypes.additionalFamily
      ? bucketTyped(row)
      : { ...row, type: rowType },
  )
  const column = rowsOfColumn(caseRecord, FAMILY_ROWS_COLUMN)
  const inGroup = (row: TabularRow): boolean =>
    additionalFamilyRowType(row) === rowType
  const others = column.filter((row) => !inGroup(row))
  const first = column.findIndex(inGroup)
  const at = first < 0 ? others.length : first
  return [...others.slice(0, at), ...group, ...others.slice(at)]
}

/**
 * The pallbearers relation after a write of one kind's rows (regular or
 * honorary), both kinds sharing one numbering. The rows keep the order the
 * write gives them, which is how a drag in the rows modal reorders them, and
 * stand as one block where that kind's first row stood; the other kind's
 * rows keep their order, a new row ends its kind's block, and the relation
 * is renumbered from 1.
 */
const pallbearersWithKind = (
  form: TabularRowForm,
  caseRecord: Record<string, unknown>,
  kept: readonly TabularRow[],
): TabularRow[] => {
  const column = orderedBySequence(rowsOfColumn(caseRecord, 'pallbearers'))
  const others = column
    .filter((row) => !rowBelongsTo(form, row))
    .map((row) => ({
      ...row,
      type: str(row.type).trim() || typeKeys.pallbearer,
    }))
  const typed = kept.map((row) => ({ ...row, type: form.rowType }))
  const first = column.findIndex((row) => rowBelongsTo(form, row))
  const at = first < 0 ? others.length : first
  return [...others.slice(0, at), ...typed, ...others.slice(at)].map(
    (row, index) => ({ ...row, sequence: String(index + 1) }),
  )
}

/**
 * The case columns a write of this field's rows changes, holding what the
 * case form would save for the same rows: the pallbearers relation keeps
 * the rows of the other kind and takes this kind's rows in the order given,
 * a family list travels with the rest of the family, an additional-family
 * write naming a group keeps every other group's rows, and the other
 * sequenced relations are numbered the way the form numbers them.
 */
export const caseColumnsWithTabularRows = (
  form: TabularRowForm,
  caseRecord: Record<string, unknown>,
  rows: readonly TabularRow[],
  rowType?: string,
): Record<string, unknown> => {
  if (form.plainStrings) {
    const names = rows.map(trimmedName).filter((name) => name !== '')
    return FAMILY_LIST_COLUMNS.includes(form.column)
      ? wholeFamily(caseRecord, { [form.column]: names })
      : { [form.column]: names }
  }
  const kept = namedRows(rows)
  if (form.column === 'pallbearers')
    return { pallbearers: pallbearersWithKind(form, caseRecord, kept) }
  if (form.column === FAMILY_ROWS_COLUMN)
    return wholeFamily(caseRecord, {
      [FAMILY_ROWS_COLUMN]: familyRowsWithGroup(caseRecord, kept, rowType),
    })
  return { [form.column]: sequenced(kept) }
}
